import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useUpdateAssistant } from '@/hooks/useAssistants'

interface EditAssistantModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  assistantId: string
  currentName: string
  currentDescription: string
}

const MAX_NAME_LENGTH = 100
const MAX_DESCRIPTION_LENGTH = 500

export function EditAssistantModal({
  open,
  onOpenChange,
  assistantId,
  currentName,
  currentDescription,
}: EditAssistantModalProps) {
  const { toast } = useToast()
  const updateAssistant = useUpdateAssistant()

  const [name, setName] = useState(currentName)
  const [description, setDescription] = useState(currentDescription)
  const [error, setError] = useState('')

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName(currentName)
      setDescription(currentDescription)
      setError('')
    }
  }, [open, currentName, currentDescription])

  const handleSave = async () => {
    // Validate
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    try {
      await updateAssistant.mutateAsync({
        assistantId,
        data: {
          name: name.trim(),
          description: description.trim(),
        },
      })
      toast({
        title: 'Assistant updated',
        description: 'Name and description have been updated.',
      })
      onOpenChange(false)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update assistant'
      setError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Assistant</DialogTitle>
          <DialogDescription>
            Update the assistant's name and description.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value.slice(0, MAX_NAME_LENGTH))
                setError('')
              }}
              placeholder="Assistant name"
            />
            <div className="flex justify-between text-xs">
              {error ? (
                <span className="text-destructive">{error}</span>
              ) : (
                <span className="text-muted-foreground">Required</span>
              )}
              <span className="text-muted-foreground">
                {name.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
              placeholder="Brief description..."
              rows={4}
            />
            <div className="flex justify-end text-xs text-muted-foreground">
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateAssistant.isPending}>
            {updateAssistant.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
